import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import api from '../../services/api';
import type { OneTimeExpenseRow } from '../../types';
import SupplierPicker, { PickedSupplier } from './SupplierPicker';

export const todayIso = () => {
  // Día de hoy en Argentina (no en UTC), para que a la noche no proponga mañana.
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Argentina/Buenos_Aires' }).format(new Date());
  return parts.slice(0, 10);
};

/** "2026-10-27T12:00:00Z" → "2026-10-27" para un input type=date. */
export const toDateInput = (iso?: string | null) => (iso ? iso.slice(0, 10) : '');

interface Props {
  open: boolean;
  onClose: () => void;
  onSaved: (e: OneTimeExpenseRow) => void;
  categories: string[];
  /** Gasto a editar; null = alta. */
  expense?: OneTimeExpenseRow | null;
  /** Alta como deuda con este proveedor (atajo desde Proveedores). */
  defaultSupplier?: PickedSupplier | null;
  defaultPayable?: boolean;
}

/**
 * Alta/edición de un gasto del día a día. Si se marca "A pagar" queda como deuda con el proveedor:
 * aparece en Orden de pago, en el Calendario el día que vence y en el Flujo de Caja.
 */
const ExpenseDialog: React.FC<Props> = ({ open, onClose, onSaved, categories, expense, defaultSupplier, defaultPayable }) => {
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(todayIso());
  const [category, setCategory] = useState('General');
  const [notes, setNotes] = useState('');
  const [payable, setPayable] = useState(false);
  const [supplier, setSupplier] = useState<PickedSupplier | null>(null);
  const [dueDate, setDueDate] = useState(todayIso());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    if (expense) {
      setDescription(expense.description);
      setAmount(String(expense.amount));
      setDate(toDateInput(expense.date));
      setCategory(expense.category || 'General');
      setNotes(expense.notes || '');
      setPayable(expense.isPayable);
      setSupplier(expense.supplierId ? { id: expense.supplierId, name: expense.supplierName || '' } : null);
      setDueDate(toDateInput(expense.dueDate) || toDateInput(expense.date));
    } else {
      setDescription('');
      setAmount('');
      setDate(todayIso());
      setCategory(categories.includes('General') ? 'General' : categories[0] || 'General');
      setNotes('');
      setPayable(!!defaultPayable);
      setSupplier(defaultSupplier || null);
      setDueDate(todayIso());
    }
  }, [open, expense, defaultSupplier, defaultPayable, categories]);

  const amountNum = Number(amount.replace(',', '.')) || 0;

  const save = async () => {
    if (!description.trim()) { setError('Poné una descripción.'); return; }
    if (amountNum <= 0) { setError('El monto tiene que ser mayor a cero.'); return; }
    if (payable && !supplier) { setError('Para cargar una deuda elegí el proveedor.'); return; }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        description: description.trim(),
        amount: amountNum,
        date,
        category,
        notes: notes.trim() || null,
        supplierId: supplier?.id ?? null,
        isPayable: payable,
        dueDate: payable ? dueDate : null,
      };
      const saved = expense
        ? await api.updateOneTimeExpense(expense.id, payload)
        : await api.createOneTimeExpense(payload);
      onSaved(saved);
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudo guardar el gasto.');
    } finally {
      setSaving(false);
    }
  };

  // La categoría del gasto puede no estar más en la lista: se ofrece igual para no perderla.
  const options = categories.includes(category) ? categories : [...categories, category];

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{expense ? 'Editar gasto' : payable ? 'Cargar deuda a proveedor' : 'Cargar gasto'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <ToggleButtonGroup exclusive fullWidth value={payable ? 'debt' : 'paid'} onChange={(_, v) => v && setPayable(v === 'debt')}>
            <ToggleButton value="paid">Ya está pagado</ToggleButton>
            <ToggleButton value="debt">A pagar (deuda)</ToggleButton>
          </ToggleButtonGroup>
          <TextField label="Descripción" value={description} onChange={e => setDescription(e.target.value)} autoFocus
            placeholder="Nafta, etiquetas, flete…" inputProps={{ maxLength: 200 }} />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField label="Monto" value={amount} onChange={e => setAmount(e.target.value)}
              inputProps={{ inputMode: 'decimal' }} sx={{ flex: 1 }} />
            <TextField label="Fecha" type="date" value={date} onChange={e => setDate(e.target.value)}
              InputLabelProps={{ shrink: true }} sx={{ flex: 1 }} />
          </Stack>
          <TextField select label="Categoría" value={category} onChange={e => setCategory(e.target.value)}>
            {options.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </TextField>
          <SupplierPicker value={supplier} onChange={setSupplier} required={payable}
            label={payable ? 'Proveedor' : 'Proveedor (opcional)'} />
          {payable && (
            <TextField label="Vence" type="date" value={dueDate} onChange={e => setDueDate(e.target.value)}
              InputLabelProps={{ shrink: true }} helperText="Aparece en Orden de pago y en el Calendario ese día." />
          )}
          <TextField label="Nota (opcional)" value={notes} onChange={e => setNotes(e.target.value)} multiline minRows={2}
            inputProps={{ maxLength: 500 }} />
          {expense?.isPayable && expense.paidAmount > 0 && payable && (
            <Alert severity="info">Ya tiene pagos registrados por ${Math.round(expense.paidAmount).toLocaleString('es-AR')}.</Alert>
          )}
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button variant="contained" onClick={save} disabled={saving}>{saving ? 'Guardando…' : 'Guardar'}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExpenseDialog;
